/* eslint-disable */
import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchWalletBalanceAsync } from 'redux/WalletSlice';
import { Box, TextField, Stack, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Button } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { checkPermissionGet } from 'utils/API/Merchant APIs/MerchantApiCalls';
import { globalNotifyError, globalNotifySuccess } from 'views/utilities/AlertToast';
import withAccessControlResetPassword from 'utils/HOC/resetPasswordHOC';
import GlobalBackButton from 'views/utilities/BackButtonGlobal';
import BcConvertModal from 'views/Components/BcConvertModal';
import Spinner from 'views/Components/LoadingSinner';
import ReKYCDialog from 'views/Components/Dialogs/ReKycDialog';
import BioAuthExistingUsers from './DmtAuthComponents/BioAuthExistingUsers';
import EditOptionComponent from '../EditOptionComponent';
import CustomerTable from './DmtReusableComponents/CustomerTable';
import { useCustomers, useMerchantCheck, useCheckCustomerExist } from './DmtHooks';

const commonStyles = { m: 0 };
const LIMIT = 10;

const CustomerList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [merchantModalOpen, setMerchantModalOpen] = useState(false);
    const [reKycDetails, setReKycDetails] = useState({ shouldOpen: false });
    const [showAuth, setShowAuth] = useState(false);
    const [dataForEkyc, setDataForEkyc] = useState([]);
    const [filteredData, setFilteredData] = useState();
    const [index, setIndex] = useState();
    const [customerDetails, setCustomerDetails] = useState({ custId: '', custName: '' });
    const [showEditModal, setShowEditModal] = useState(false);

    const shouldshowEsign = localStorage.getItem('shouldshowEsign');
    const data = location?.state?.createSuccessData;
    const hasShownSuccessNotification = useRef(false);

    if (data && !hasShownSuccessNotification.current) {
        globalNotifySuccess('Customer created successfully!');
        hasShownSuccessNotification.current = true;
    }

    const { checkCustomerExist, checkExistLoading } = useCheckCustomerExist(search, data);
    const { data: customersData, isLoading: isLoadingTable } = useCustomers(page, search, navigate);
    const { data: merchantCheck, isLoading: isCheckLoading } = useMerchantCheck(setMerchantModalOpen);

    const customers = useMemo(() => customersData?.customers || [], [customersData]);
    const totalCount = useMemo(() => customersData?.totalCount || 0, [customersData]);
    const filteredCustomers = useMemo(() => customers.filter((item) => item?.mobileNumber?.includes(search)), [customers, search]);
    const paginatedData = useMemo(() => filteredCustomers.slice((page - 1) * LIMIT, page * LIMIT), [filteredCustomers, page]);

    const mobileValidationSchema = Yup.object().shape({
        mobile: Yup.string()
            .matches(/^[0-9]+$/, 'Mobile number must contain only digits')
            .length(10, 'Please enter a valid 10 digit mobile number')
            .required('Mobile number is required')
    });

    const verifyRekycStatus = useCallback(async () => {
        const permissionResponse = await checkPermissionGet();
        if (permissionResponse?.status === 200) {
            const isWhitelabel = permissionResponse?.data.whitelabel;
            setReKycDetails({ shouldOpen: !isWhitelabel });
        }
    }, []);

    const handleBcModalClose = useCallback(() => {
        setMerchantModalOpen(false);
    }, []);

    const handleTransferClick = useCallback(
        (rowData, index) => {
            localStorage.setItem('sendernumber', rowData?.mobileNumber);

            if (rowData?.ekycStatus === true) {
                navigate('/moneyTransfer1/moneyTransfer', { state: { data: rowData, filteredData: customers, index } });
            } else {
                setShowAuth(true);
            }
        },
        [customers, navigate]
    );

    const showModal = useCallback((newValue) => {
        setShowAuth(newValue);
    }, []);

    const showEdit = useCallback((newValue) => {
        setShowEditModal(newValue);
    }, []);

    const buttonView = useCallback(
        (item, index) => {
            localStorage.setItem('sendernumber', item?.mobileNumber);
            setDataForEkyc(item);
            setFilteredData(customers);
            setIndex(index);

            if (item?.ekycStatus === true) {
                navigate('/moneyTransfer1/viewBeneficiary', { state: { item, filteredData: filteredCustomers, index } });
            } else {
                setShowAuth(true);
            }
        },
        [customers, filteredCustomers, navigate]
    );

    const handleEditClick = useCallback((row) => {
        setShowEditModal(true);
        setCustomerDetails({ custId: row?.id, custName: row?.customerName });
    }, []);

    useEffect(() => {
        dispatch(fetchWalletBalanceAsync());
        verifyRekycStatus();
    }, []);

    const submitHandler = (values) => {
        if (filteredCustomers[0]?.mobileNumber === values.mobile) {
            globalNotifyError('Customer Already Exists!');
        } else {
            checkCustomerExist();
        }
    };

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    height: 'auto',
                    backgroundColor: '#fff',
                    ...commonStyles,
                    borderRadius: 2,
                    boxShadow: 2,
                    padding: '20px 10px',
                    '@media screen and (min-width: 600px)': {
                        padding: 2
                    }
                }}
            >
                <Typography variant="h3">
                    <GlobalBackButton />
                    Domestic Money Transfer
                </Typography>

                <Box sx={{ marginTop: '10px' }}>
                    <Link to="/merchant-home" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <HomeOutlined />
                    </Link>
                    <Typography component="span" style={{ margin: '0 10px' }}>
                        &gt;
                    </Typography>
                    <Typography component="span">Domestic Money Transfer</Typography>
                </Box>
            </Box>

            <Formik initialValues={{ mobile: search }} validationSchema={mobileValidationSchema} onSubmit={submitHandler}>
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                    <Form>
                        <Box
                            sx={{
                                '& > :not(style)': { mb: 1, mt: 2, width: '30ch' }
                            }}
                            noValidate
                            autoComplete="off"
                        >
                            <Stack direction="row" spacing={1}>
                                <Tooltip title="Please ensure you enter the Aadhaar-linked mobile number here, as the OTP required for verification will be sent to the mobile number associated with the provided Aadhaar number.">
                                    <TextField
                                        label="Search with Mobile"
                                        name="mobile"
                                        value={values.mobile}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, ''); // Ensure only digits
                                            if (val.length <= 10) {
                                                setSearch(val);
                                                setPage(1); // Reset page on search
                                                // Update formik's field value manually
                                                e.target.value = val;
                                                handleChange(e);
                                            }
                                        }}
                                        variant="outlined"
                                        helperText={touched.mobile && errors.mobile}
                                        error={touched.mobile && Boolean(errors.mobile)}
                                        onBlur={handleBlur}
                                    />
                                </Tooltip>

                                <Box sx={{ '& > :not(style)': {}, paddingTop: 1 }}>
                                    <Tooltip title="Click to create new customer">
                                        <Button
                                            loading={checkExistLoading}
                                            onClick={handleSubmit}
                                            shape="circle"
                                            type="primary"
                                            icon={<AddIcon />}
                                        />
                                    </Tooltip>
                                </Box>
                            </Stack>
                        </Box>
                    </Form>
                )}
            </Formik>

            <Spinner loading={isCheckLoading} />
            <CustomerTable
                data={paginatedData}
                page={page}
                limit={LIMIT}
                totalCount={totalCount}
                onPageChange={(e, value) => setPage(value)}
                onEdit={handleEditClick}
                onView={buttonView}
                onTransfer={handleTransferClick}
                isLoading={isLoadingTable}
            />

            <ToastContainer />
            {merchantModalOpen && (
                <BcConvertModal
                    isOpen={merchantModalOpen}
                    handleclose={handleBcModalClose}
                    isPending={merchantCheck?.isPending}
                    serviceName={'dmt'}
                    shouldshowEsign={shouldshowEsign}
                />
            )}
            {reKycDetails.shouldOpen && <ReKYCDialog />}
            {showAuth && <BioAuthExistingUsers showModal={showModal} datas={dataForEkyc} index={index} filteredData={filteredData} />}
            {showEditModal && (
                <EditOptionComponent showEdit={showEdit} custId={customerDetails.custId} custName={customerDetails.custName} />
            )}
        </>
    );
};

export default withAccessControlResetPassword(CustomerList);
